import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const SicInOutInventario = ({ empresa, identificacion }) => {
 
  // Estados para la búsqueda y datos
  const [searchTerm, setSearchTerm] = useState('');
  const [detalles, setDetalles] = useState([]);
  const [detallesSeleccionados, setDetallesSeleccionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errores, setErrores] = useState({});
  const [mostrarErrores, setMostrarErrores] = useState(false);
  
  // Referencias para el desplazamiento horizontal
  const tableContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  // Función para obtener el email del usuario actual
  const getCurrentUserEmail = () => {
    try {
      return auth.currentUser?.email || 'sistema@empresa.com';
    } catch (error) {
      console.log('Error obteniendo usuario:', error);
      return 'sistema@empresa.com';
    }
  };

  // Obtener todos los detalles de Firebase al cargar el componente
  const obtenerTodosLosDetalles = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'SicDetalleFactura'));
      const querySnapshot = await getDocs(q);
      const detallesData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        detallesData.push({ 
          id: doc.id, 
          ...data,
          inventario: data.Inventario !== undefined ? data.Inventario : 
                     data.inventario !== undefined ? data.inventario : true
        });
      });
      
      setDetalles(detallesData);
    } catch (error) {
      console.error('Error obteniendo detalles:', error);
    }
    setLoading(false);
  };

  // Buscar detalles basado en el término de búsqueda
  const buscarDetalles = async () => {
    setLoading(true);
    try {
      if (searchTerm.trim() === '') {
        obtenerTodosLosDetalles();
      } else {
        const searchLower = searchTerm.toLowerCase();
        const allDocs = query(collection(db, 'SicDetalleFactura'));
        const querySnapshot = await getDocs(allDocs);
        const detallesData = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (
            data.detalle?.toLowerCase().includes(searchLower) ||
            data.codigo?.toLowerCase().includes(searchLower) ||
            data.codigoComercialCodigo?.toLowerCase().includes(searchLower)
          ) {
            detallesData.push({ 
              id: doc.id, 
              ...data,
              inventario: data.Inventario !== undefined ? data.Inventario : 
                         data.inventario !== undefined ? data.inventario : true
            });
          }
        });
        
        setDetalles(detallesData);
      }
    } catch (error) {
      console.error('Error buscando detalles:', error);
    }
    setLoading(false);
  };

  // Agregar detalle a la tabla de entrada
  const seleccionarDetalle = (detalle) => {
    const nuevoDetalle = {
      ...detalle,
      numeroConsecutivo: `${identificacion}${detalle.codigoComercialCodigo || ''}`,
      emisorNumeroIdentificacion: identificacion,
      emisorNombre: empresa || 'Nombre no disponible',
      tipoFactura: 'Entrada inventario',
      codigoMoneda: 'CRC',
      tipoCambio: '1',
      fechaEmision: new Date().toISOString().split('T')[0],
      fechaModificacion: serverTimestamp(),
      fechaSubida: serverTimestamp(),
      numeroLinea: (detallesSeleccionados.length + 1).toString(),
      cantidad: 1,
      precioUnitario: 0,
      impuestoTarifa: 0,
      impuestoMonto: 0,
      montoTotalLinea: 0,
      responsable: getCurrentUserEmail(), // ← CAMBIO AQUÍ
      codigoEmpresa: detalle.codigoComercialCodigo || '',
      justificacion: ''
    };
    
    setDetallesSeleccionados([...detallesSeleccionados, nuevoDetalle]);
  };

  // Actualizar campos editables
  const actualizarDetalle = (index, campo, valor) => {
    const nuevosDetalles = [...detallesSeleccionados];
    nuevosDetalles[index][campo] = valor;
    
    if (['cantidad', 'precioUnitario', 'impuestoTarifa'].includes(campo)) {
      const cantidad = parseFloat(nuevosDetalles[index].cantidad) || 0;
      const precioUnitario = parseFloat(nuevosDetalles[index].precioUnitario) || 0;
      const impuestoTarifa = parseFloat(nuevosDetalles[index].impuestoTarifa) || 0;
      
      nuevosDetalles[index].impuestoMonto = cantidad * precioUnitario * (impuestoTarifa / 100);
      nuevosDetalles[index].montoTotalLinea = cantidad * precioUnitario + nuevosDetalles[index].impuestoMonto;
    }
    
    setDetallesSeleccionados(nuevosDetalles);
    
    // Limpiar error si el campo se completa
    if (valor && errores[index] && errores[index][campo]) {
      const nuevosErrores = {...errores};
      delete nuevosErrores[index][campo];
      if (Object.keys(nuevosErrores[index]).length === 0) {
        delete nuevosErrores[index];
      }
      setErrores(nuevosErrores);
    }
  };

  // Validar campos antes de guardar
  const validarCampos = () => {
    const nuevosErrores = {};
    
    detallesSeleccionados.forEach((detalle, index) => {
      const errorCampos = {};
      
      if (!detalle.cantidad || parseFloat(detalle.cantidad) <= 0) {
        errorCampos.cantidad = 'Cantidad debe ser > 0';
      }
      
      if (!detalle.precioUnitario || parseFloat(detalle.precioUnitario) < 0) {
        errorCampos.precioUnitario = 'Precio no puede ser negativo';
      }
      
      if (detalle.impuestoTarifa === '' || parseFloat(detalle.impuestoTarifa) < 0) {
        errorCampos.impuestoTarifa = 'Tarifa no puede ser negativa';
      }
      
      if (!detalle.justificacion.trim()) {
        errorCampos.justificacion = 'Justificación requerida';
      }
      
      if (Object.keys(errorCampos).length > 0) {
        nuevosErrores[index] = errorCampos;
      }
    });
    
    setErrores(nuevosErrores);
    setMostrarErrores(Object.keys(nuevosErrores).length > 0);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Guardar en Firebase
  const procesarFactura = async () => {
    if (!validarCampos()) {
      return;
    }
    
    try {
      for (const detalle of detallesSeleccionados) {
        // Asegurar que el responsable esté actualizado
        const detalleConResponsable = {
          ...detalle,
          responsable: getCurrentUserEmail(), // ← Asegurar email actualizado
          fechaModificacion: serverTimestamp()
        };
        await addDoc(collection(db, 'DetalleFactura'), detalleConResponsable);
      }
      alert('Ajuste procesado exitosamente');
      setDetallesSeleccionados([]);
      setErrores({});
      setMostrarErrores(false);
    } catch (error) {
      console.error('Error procesando factura:', error);
      alert('Error al procesar la factura');
    }
  };

  // Funciones para el desplazamiento horizontal con arrastre
  const startDragging = (e) => {
    isDraggingRef.current = true;
    startXRef.current = e.pageX - tableContainerRef.current.offsetLeft;
    scrollLeftRef.current = tableContainerRef.current.scrollLeft;
    tableContainerRef.current.style.cursor = 'grabbing';
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    if (tableContainerRef.current) {
      tableContainerRef.current.style.cursor = 'grab';
    }
  };

  const whileDragging = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const x = e.pageX - tableContainerRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2;
    tableContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  // Cargar todos los detalles al iniciar
  useEffect(() => {
    obtenerTodosLosDetalles();
  }, []);

  // Agregar event listeners para el arrastre
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('mousedown', startDragging);
      container.addEventListener('mousemove', whileDragging);
      container.addEventListener('mouseup', stopDragging);
      container.addEventListener('mouseleave', stopDragging);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousedown', startDragging);
        container.removeEventListener('mousemove', whileDragging);
        container.removeEventListener('mouseup', stopDragging);
        container.removeEventListener('mouseleave', stopDragging);
      }
    };
  }, [detallesSeleccionados]);

  return (
    <div className="container-fluid py-4" style={{ height: '100vh' }}>
      <h1 className="text-center mb-4">
        <span style={{marginRight: '0.5rem'}}>📦</span>
        Ajustes al Inventario
      </h1>
      
      {/* Mensaje de error general */}
      {mostrarErrores && (
        <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert">
          <strong>Error de validación:</strong> Por favor, complete todos los campos requeridos correctamente.
          <button type="button" className="btn-close" onClick={() => setMostrarErrores(false)}></button>
        </div>
      )}
      
      {/* Tabla 1: Búsqueda de SicDetalleFactura */}
      <div className="card mb-3 shadow-sm" style={{ height: '35%' }}>
        <div className="card-header text-white py-2" style={{ backgroundColor: '#3b82f6' }}>
          <h5 className="mb-0">
            <span style={{marginRight: '0.5rem'}}>📚</span>
            Búsqueda en Catálogo de Productos
          </h5>
        </div>
        <div className="card-body d-flex flex-column p-2">
          <div className="d-flex gap-2 align-items-center mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por detalle, código o código comercial..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarDetalles()}
              style={{ 
                fontSize: '14px',
                padding: '8px 12px',
                width: '725px',
                flex: 'none'
              }}
            />
            <button 
              className="btn btn-sm"
              onClick={buscarDetalles}
              disabled={loading}
              style={{ 
                backgroundColor: '#3b82f6', 
                color: 'white',
                fontSize: '14px',
                padding: '8px 16px',
                whiteSpace: 'nowrap',
                flex: 'none'
              }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setSearchTerm('');
                obtenerTodosLosDetalles();
              }}
              style={{ 
                fontSize: '14px', 
                padding: '8px 16px',
                whiteSpace: 'nowrap',
                flex: 'none'
              }}
            >
              Limpiar
            </button>
          </div>
          
          <div className="table-responsive flex-grow-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <table className="table table-sm table-hover mb-0">
              <thead className="sticky-top">
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th width="60" className="text-center" style={{ backgroundColor: '#e5e7eb', padding: '8px' }}>Inv</th>
                  <th width="120" style={{ backgroundColor: '#e5e7eb', padding: '8px' }}>Código CAByS</th>
                  <th width="120" style={{ backgroundColor: '#e5e7eb', padding: '8px' }}>Código Comercial</th>
                  <th style={{ backgroundColor: '#e5e7eb', padding: '8px' }}>Detalle</th>
                  <th width="100" className="text-center" style={{ backgroundColor: '#e5e7eb', padding: '8px' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {detalles.length > 0 ? (
                  detalles.map((detalle, index) => (
                    <tr key={detalle.id || index}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={detalle.inventario || false}
                          className="form-check-input"
                          readOnly
                          style={{ transform: 'scale(1.2)' }}
                        />
                      </td>
                      <td className="text-truncate" title={detalle.codigo}>
                        {detalle.codigo || 'N/A'}
                      </td>
                      <td className="text-truncate" title={detalle.codigoComercialCodigo}>
                        {detalle.codigoComercialCodigo || 'N/A'}
                      </td>
                      <td className="text-truncate" title={detalle.detalle}>
                        {detalle.detalle || 'N/A'}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          onClick={() => seleccionarDetalle(detalle)}
                          disabled={detallesSeleccionados.some(d => d.id === detalle.id)}
                          style={{ 
                            fontSize: '12px', 
                            padding: '2px 6px',
                            backgroundColor: detallesSeleccionados.some(d => d.id === detalle.id) ? '#6b7280' : '#10b981',
                            color: 'white',
                            border: 'none'
                          }}
                        >
                          {detallesSeleccionados.some(d => d.id === detalle.id) ? '✓' : 'Agregar'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {loading ? 'Cargando...' : 'No se encontraron resultados'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Tabla 2: Detalles seleccionados */}
      <div className="card shadow-sm" style={{ height: '60%' }}>
        <div className="card-header text-white py-2" style={{ backgroundColor: '#3b82f6' }}>
          <h5 className="mb-0">
            <span style={{marginRight: '0.5rem'}}>📦</span>
            Ajustes al Inventario
          </h5>
        </div>
        <div className="card-body d-flex flex-column p-2">
          <div 
            ref={tableContainerRef}
            className="table-responsive flex-grow-1" 
            style={{ 
              maxHeight: '350px', 
              overflow: 'auto',
              cursor: 'grab'
            }}
          >
            <table className="table table-sm table-bordered mb-0" style={{ minWidth: '1800px' }}>
              <colgroup>
                <col style={{ width: '40px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '1200px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '800px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '60px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '80px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '100px' }} />
                <col style={{ width: '250px' }} />
              </colgroup>
              <thead className="sticky-top">
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th className="text-center">Inv</th>
                  <th>Tipo Factura</th>
                  <th>N° Consecutivo</th>
                  <th>Código CAByS</th>
                  <th>Código Comercial</th>
                  <th>Detalle</th>
                  <th>ID Emisor</th>
                  <th>Nombre Emisor</th>
                  <th>Fecha</th>
                  <th className="text-center">Línea</th>
                  <th className="text-end">Cantidad</th>
                  <th className="text-end">Precio</th>
                  <th className="text-end">Tarifa %</th>
                  <th className="text-end">Impuesto</th>
                  <th className="text-end">Total</th>
                  <th>Justificación</th>
                </tr>
              </thead>
              <tbody>
                {detallesSeleccionados.length > 0 ? (
                  detallesSeleccionados.map((detalle, index) => (
                    <tr key={index}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={detalle.inventario || false}
                          className="form-check-input"
                          readOnly
                          style={{ transform: 'scale(1.2)' }}
                        />
                      </td>
                      <td>
                        <select
                          className={`form-select form-select-sm ${errores[index]?.tipoFactura ? 'border-danger' : ''}`}
                          value={detalle.tipoFactura}
                          onChange={(e) => actualizarDetalle(index, 'tipoFactura', e.target.value)}
                          style={{ fontSize: '12px' }}
                        >
                          <option value="Entrada inventario">Entrada</option>
                          <option value="Salida inventario">Salida</option>
                        </select>
                      </td>
                      <td className="text-truncate" title={detalle.numeroConsecutivo}>
                        <small>{detalle.numeroConsecutivo}</small>
                      </td>
                      <td className="text-truncate" title={detalle.codigo}>
                        <small>{detalle.codigo}</small>
                      </td>
                      <td className="text-truncate" title={detalle.codigoComercialCodigo}>
                        <small>{detalle.codigoComercialCodigo}</small>
                      </td>
                      <td className="text-truncate" title={detalle.detalle}>
                        <small>{detalle.detalle}</small>
                      </td>
                      <td className="text-truncate" title={detalle.emisorNumeroIdentificacion}>
                        <small>{detalle.emisorNumeroIdentificacion}</small>
                      </td>
                      <td className="text-truncate" title={detalle.emisorNombre}>
                        <small style={{ color: '#007bff', fontWeight: 'bold' }}>
                          {detalle.emisorNombre}
                        </small>
                      </td>
                      <td>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={detalle.fechaEmision}
                          onChange={(e) => actualizarDetalle(index, 'fechaEmision', e.target.value)}
                          style={{ fontSize: '12px' }}
                        />
                      </td>
                      <td className="text-center">
                        <small>{detalle.numeroLinea}</small>
                      </td>
                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${errores[index]?.cantidad ? 'border-danger' : ''}`}
                          value={detalle.cantidad}
                          onChange={(e) => actualizarDetalle(index, 'cantidad', e.target.value)}
                          min="1"
                          style={{ fontSize: '12px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${errores[index]?.precioUnitario ? 'border-danger' : ''}`}
                          value={detalle.precioUnitario}
                          onChange={(e) => actualizarDetalle(index, 'precioUnitario', e.target.value)}
                          min="0"
                          step="0.01"
                          style={{ fontSize: '12px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${errores[index]?.impuestoTarifa ? 'border-danger' : ''}`}
                          value={detalle.impuestoTarifa}
                          onChange={(e) => actualizarDetalle(index, 'impuestoTarifa', e.target.value)}
                          min="0"
                          step="0.01"
                          style={{ fontSize: '12px' }}
                        />
                      </td>
                      <td className="text-end">
                        <small>₡{detalle.impuestoMonto?.toFixed(2) || '0.00'}</small>
                      </td>
                      <td className="text-end">
                        <small style={{ fontWeight: 'bold' }}>
                          ₡{detalle.montoTotalLinea?.toFixed(2) || '0.00'}
                        </small>
                      </td>
                      <td>
                        <textarea
                          className={`form-control form-control-sm ${errores[index]?.justificacion ? 'border-danger' : ''}`}
                          value={detalle.justificacion}
                          onChange={(e) => actualizarDetalle(index, 'justificacion', e.target.value)}
                          rows="2"
                          placeholder="Justificación del ajuste"
                          style={{ fontSize: '12px', resize: 'vertical' }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="16" className="text-center">
                      No hay detalles seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Mensajes de error debajo de la tabla */}
          {mostrarErrores && (
            <div className="mt-2">
              {detallesSeleccionados.map((detalle, index) => (
                <div key={index}>
                  {errores[index] && Object.keys(errores[index]).length > 0 && (
                    <div className="alert alert-danger py-1 mb-1" style={{ fontSize: '12px' }}>
                      <strong>Línea {detalle.numeroLinea}:</strong>{' '}
                      {Object.entries(errores[index]).map(([campo, mensaje]) => (
                        <span key={campo} className="me-2">
                          {mensaje}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {detallesSeleccionados.length > 0 && (
            <div className="text-end mt-2">
              <button 
                className="btn btn-sm"
                onClick={procesarFactura}
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                Procesar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SicInOutInventario;